import { getAccessToken, AuthNetworkError } from './auth.ts';
import { handleApiError, ApiError } from './errors.ts';
import type {
  Album,
  RenewalKind,
  RedeemOTPRequest,
  RedeemOTPResponse,
  ShareRequest,
  ShareResponse,
  AlbumListResponse,
  PaymentSessionRequest,
  PaymentSessionResponse,
  UploadInitRequest,
  UploadInitResponse,
  UploadCompleteRequest,
  UploadCompleteResponse,
  DownloadInitResponse,
  ExportStatusResponse,
  DeletePhotosRequest,
  DeletePhotosResponse,
  RenamePhotoRequest,
  RenamePhotoResponse,
  SetAlbumCoverRequest,
  SetAlbumCoverResponse,
  ViewLinkResponse,
  ViewAlbumResponse,
  MemoirListResponse,
  AccessLogResponse,
  AccountClosureView,
} from '../../../../types';

export interface ApiClientConfig {
  baseUrl: string;
  timeout?: number;
}

export class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.timeout = config.timeout || 30000; // 30 second default timeout
  }

  /**
   * Make authenticated HTTP request with proper error handling
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    requireAuth = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Setup headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Setup request with timeout
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      // Inside the try on purpose: getAccessToken() can *reject* (transport
      // failure while refreshing), and outside it that rejection would surface
      // to callers as a raw Error instead of an ApiError.
      if (requireAuth) {
        let token: string | null;
        try {
          token = await getAccessToken();
        } catch (authError) {
          if (authError instanceof AuthNetworkError) {
            // Offline, not signed out. Report it as a network problem so the
            // UI doesn't tell the user to log in again.
            throw new ApiError(authError.message, 0, 'NETWORK_ERROR');
          }
          throw authError;
        }
        if (!token) {
          throw new ApiError('Authentication required', 401, 'AUTH_REQUIRED');
        }
        headers.Authorization = `Bearer ${token}`;
      }

      timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-JSON responses (like 404s)
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        if (!response.ok) {
          throw new ApiError(
            `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            'HTTP_ERROR'
          );
        }
        return {} as T;
      }

      const data = await response.json();

      if (!response.ok) {
        // Handle structured error responses from Workers
        const errorData = data as { error?: string; code?: string; details?: any };
        throw new ApiError(
          errorData.error || `HTTP ${response.status}`,
          response.status,
          errorData.code || 'API_ERROR',
          errorData.details
        );
      }

      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors, timeouts, etc.
      return handleApiError(error);
    }
  }

  /**
   * Share an album and generate an OTP
   */
  async shareAlbum(uuid: string, request: ShareRequest): Promise<ShareResponse> {
    return this.makeRequest<ShareResponse>(`/api/album/${uuid}/share`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Make a server-relative path absolute. The public view payload returns
   * image and download URLs as paths (`/api/album/...`); in development the
   * SPA and the Worker are on different origins, so rendering them raw
   * silently 404s against the Vite dev server.
   */
  toAbsolute(path: string): string {
    return path.startsWith('http') ? path : `${this.baseUrl}${path}`;
  }

  async createViewLink(
    uuid: string,
    request: { expiresInDays?: number | null },
  ): Promise<ViewLinkResponse> {
    return this.makeRequest<ViewLinkResponse>(`/api/album/${uuid}/view-link`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /** Resolves null when the album has no link — a 404 here is a state, not an error. */
  async getViewLink(uuid: string): Promise<ViewLinkResponse | null> {
    try {
      return await this.makeRequest<ViewLinkResponse>(`/api/album/${uuid}/view-link`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  async revokeViewLink(uuid: string): Promise<void> {
    await this.makeRequest<{ success: boolean }>(`/api/album/${uuid}/view-link`, {
      method: 'DELETE',
    });
  }

  /** Public path: no Bearer token, no session required. */
  async getViewAlbum(token: string): Promise<ViewAlbumResponse> {
    return this.makeRequest<ViewAlbumResponse>(
      `/api/view/${encodeURIComponent(token)}`,
      {},
      false,
    );
  }

  /**
   * Redeem an OTP code to claim an album
   */
  async redeemOTP(request: RedeemOTPRequest): Promise<RedeemOTPResponse> {
    return this.makeRequest<RedeemOTPResponse>('/api/redeem', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get a specific album by UUID
   */
  async getAlbum(uuid: string): Promise<{ album: Album; files: any[] }> {
    return this.makeRequest<{ album: Album; files: any[] }>(`/api/album/${uuid}`);
  }

  /**
   * Get all albums for the authenticated user
   */
  async getAlbums(): Promise<AlbumListResponse> {
    return this.makeRequest<AlbumListResponse>('/api/albums');
  }

  /**
   * Initialize upload - get presigned URLs for direct R2 upload
   */
  async uploadInit(request: UploadInitRequest): Promise<UploadInitResponse> {
    return this.makeRequest<UploadInitResponse>('/api/upload/init', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Complete upload - confirm files were uploaded and update album
   */
  async uploadComplete(request: UploadCompleteRequest): Promise<UploadCompleteResponse> {
    return this.makeRequest<UploadCompleteResponse>('/api/upload/complete', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Upload a single file directly to R2 using presigned URL or local endpoint
   */
  async uploadFileToR2(
    presignedUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
    // The file's own SSE-C key from /upload/init (SOL-203). R2 refuses the PUT
    // without it; the dev endpoint encrypts server-side and sends none.
    sse?: { algorithm: string; key: string; keyMd5: string }
  ): Promise<void> {
    // Check if this is a local endpoint (needs auth) vs presigned URL (no auth needed)
    const isLocalEndpoint = presignedUrl.startsWith(this.baseUrl);

    // Get auth token BEFORE creating the XHR promise (fixes async executor issue)
    let authToken: string | null = null;
    if (isLocalEndpoint) {
      authToken = await getAccessToken();
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Inactivity watchdog, not a total-duration timeout: abort only if
      // NO upload progress arrives for INACTIVITY_TIMEOUT_MS. A fixed
      // total-duration cap would fail a merely slow-but-progressing
      // transfer - this app's R2 uploads go up to 500MB (RAW photo
      // formats), and at a realistic 5 Mbps upstream that's ~13 minutes,
      // at 2 Mbps ~33 minutes, entirely normal on residential connections.
      // The R2 PUT is single-shot and non-resumable, so timing that out
      // would throw away everything already sent and force a full
      // re-upload - turning "slow connection" into "cannot upload large
      // photos at all" to guard against a rare wedged panel. Watching for
      // inactivity instead never penalises a transfer for being large or
      // slow, only for having genuinely stopped.
      //
      // 60s of zero progress: long enough to ride out normal congestion or
      // TCP renegotiation blips without false-failing a real transfer,
      // short enough that a truly wedged upload (and the now-undismissable
      // -while-busy panel behind it - see usePresignedUpload) recovers in
      // about a minute instead of hanging indefinitely.
      const INACTIVITY_TIMEOUT_MS = 60 * 1000;
      let lastProgressAt = Date.now();
      let watchdog: ReturnType<typeof setInterval> | undefined;
      const clearWatchdog = () => {
        if (watchdog !== undefined) {
          clearInterval(watchdog);
          watchdog = undefined;
        }
      };

      xhr.upload.addEventListener('progress', (event) => {
        lastProgressAt = Date.now();
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        clearWatchdog();
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new ApiError(`Upload failed: ${xhr.statusText}`, xhr.status, 'UPLOAD_ERROR'));
        }
      });

      xhr.addEventListener('error', () => {
        clearWatchdog();
        reject(new ApiError('Upload failed: Network error', 0, 'NETWORK_ERROR'));
      });

      // Our own watchdog is the only thing that calls xhr.abort() in this
      // codebase (no AbortController is wired up here), so an 'abort'
      // event unambiguously means "stalled", not an external cancellation.
      xhr.addEventListener('abort', () => {
        clearWatchdog();
        reject(new ApiError('Upload stalled: no progress for 60s', 0, 'STALLED_ERROR'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      if (sse) {
        xhr.setRequestHeader('x-amz-server-side-encryption-customer-algorithm', sse.algorithm);
        xhr.setRequestHeader('x-amz-server-side-encryption-customer-key', sse.key);
        xhr.setRequestHeader('x-amz-server-side-encryption-customer-key-md5', sse.keyMd5);
      }

      // Add auth header for local endpoints
      if (authToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
      }

      // Started after open()/setRequestHeader(), before send() - same
      // ordering discipline a `xhr.timeout` property would need (setting
      // XHR-lifecycle-related config before open() risks being silently
      // ignored on some engines). Not strictly required for a plain
      // interval, but keeping it consistent avoids relying on engine-
      // specific pre-open behavior.
      watchdog = setInterval(() => {
        if (Date.now() - lastProgressAt >= INACTIVITY_TIMEOUT_MS) {
          clearWatchdog();
          xhr.abort();
        }
      }, 1000);

      xhr.send(file);
    });
  }



  /**
   * Ask the server whether a selection can be zipped synchronously. Returns
   * either a `sync` tier (a signed, expiring URL ready to navigate to) or an
   * `async` tier (phase 2 background job; today's callers fall back to the
   * per-file download loop).
   */
  async downloadInit(albumUuid: string, fileIds: string[]): Promise<DownloadInitResponse> {
    return this.makeRequest<DownloadInitResponse>(`/api/album/${albumUuid}/download/init`, {
      method: 'POST',
      body: JSON.stringify({ fileIds }),
    });
  }

  /**
   * Poll an async export's status: `preparing` (with progress), `ready` (with
   * a presigned R2 URL freshly minted by THIS call), or `failed`. Called both
   * right after starting an export and from the export status page a user
   * reaches from the "your download is ready" email — the server, not this
   * client, decides which of the three applies.
   */
  async getExportStatus(albumUuid: string, hash: string): Promise<ExportStatusResponse> {
    return this.makeRequest<ExportStatusResponse>(
      `/api/album/${albumUuid}/export/${encodeURIComponent(hash)}`,
    );
  }

  /**
   * Download a file from an album
   */
  async downloadFile(albumUuid: string, fileName: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/api/album/${albumUuid}/file/${fileName}`, {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new ApiError(
        error || `Failed to download ${fileName}`,
        response.status,
        'DOWNLOAD_ERROR'
      );
    }

    return response.blob();
  }

  /**
   * Create Stripe payment session for paid signup
   */
  async createPaymentSession(request: PaymentSessionRequest): Promise<PaymentSessionResponse> {
    return this.makeRequest<PaymentSessionResponse>('/api/payment/create-session', {
      method: 'POST',
      body: JSON.stringify(request),
    }, false); // No auth required for signup
  }

  /** The signed-in user's Memoirs, newest first (SOL-204). */
  async getMemoirs(): Promise<MemoirListResponse> {
    return this.makeRequest<MemoirListResponse>('/api/memoirs', {})
  }

  /** One page of a Memoir's access log, newest first (SOL-203). */
  async getAccessLog(memoirId: string, cursor?: string): Promise<AccessLogResponse> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
    return this.makeRequest<AccessLogResponse>(`/api/memoir/${encodeURIComponent(memoirId)}/access-log${query}`, {})
  }

  /**
   * A Stripe Checkout URL for buying another Memoir while signed in (SOL-204).
   * The Memoir is created by the webhook once payment settles, not here.
   */
  async createMemoirCheckout(memoirName: string, successUrl: string, cancelUrl: string): Promise<{ url: string }> {
    return this.makeRequest<{ url: string }>('/api/memoir/checkout', {
      method: 'POST',
      body: JSON.stringify({ memoirName, success_url: successUrl, cancel_url: cancelUrl }),
    })
  }

  /**
   * Pay again for an unpaid Memoir (SOL-206): a Checkout URL whose payment
   * reactivates THIS Memoir rather than creating one.
   */
  async createMemoirRepurchase(memoirId: string, successUrl: string, cancelUrl: string): Promise<{ url: string }> {
    return this.makeRequest<{ url: string }>('/api/memoir/checkout', {
      method: 'POST',
      body: JSON.stringify({ memoirId, success_url: successUrl, cancel_url: cancelUrl }),
    })
  }

  /**
   * Buy another year for a Memoir (SOL-185), priced by its size. Resolves to
   * the Stripe Checkout URL.
   */
  async renewMemoir(memoirId: string, kind: RenewalKind, successUrl: string, cancelUrl: string): Promise<{ url: string }> {
    return this.makeRequest<{ url: string }>(`/api/memoir/${encodeURIComponent(memoirId)}/renew`, {
      method: 'POST',
      body: JSON.stringify({ kind, success_url: successUrl, cancel_url: cancelUrl }),
    })
  }

  /** What a renewal costs, and when the window opens (SOL-185). Never a size. */
  async getRenewalPrice(memoirId: string): Promise<{ readOnlyEur: number; writableEur: number; opensAt: string }> {
    return this.makeRequest<{ readOnlyEur: number; writableEur: number; opensAt: string }>(
      `/api/memoir/${encodeURIComponent(memoirId)}/renewal`, {},
    )
  }

  /**
   * Refund a Memoir within 30 days of buying it (SOL-206). Moves the money
   * only; the Memoir turns read-only once Stripe confirms, seconds later.
   */
  async refundMemoir(memoirId: string): Promise<{ status: string }> {
    return this.makeRequest<{ status: string }>(`/api/memoir/${encodeURIComponent(memoirId)}/refund`, {
      method: 'POST',
    })
  }

  /**
   * Invite someone to take a Memoir over (SOL-185): by email, or by the user id
   * of a client who opened an album in it. An unknown address gets an account
   * and a mail telling them to sign in at memoar.me.
   */
  async inviteToMemoir(
    memoirId: string,
    body: { email: string } | { userId: string },
  ): Promise<{ invite: { email: string; invitedAt: string } }> {
    return this.makeRequest<{ invite: { email: string; invitedAt: string } }>(
      `/api/memoir/${encodeURIComponent(memoirId)}/invite`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  /** The owner withdraws the open invitation (SOL-185). */
  async withdrawMemoirInvite(memoirId: string): Promise<{ ok: true }> {
    return this.makeRequest<{ ok: true }>(`/api/memoir/${encodeURIComponent(memoirId)}/invite`, {
      method: 'DELETE',
    })
  }

  /** The invitee accepts the invitation (SOL-185). */
  async acceptMemoirInvite(memoirId: string): Promise<{ ok: true }> {
    return this.makeRequest<{ ok: true }>(`/api/memoir/${encodeURIComponent(memoirId)}/invite/accept`, {
      method: 'POST',
    })
  }

  /** The invitee declines the invitation (SOL-185). */
  async declineMemoirInvite(memoirId: string): Promise<{ ok: true }> {
    return this.makeRequest<{ ok: true }>(`/api/memoir/${encodeURIComponent(memoirId)}/invite/decline`, {
      method: 'POST',
    })
  }

  /** The owner hands the Memoir over to the invitee who accepted (SOL-185). */
  async handOverMemoir(memoirId: string): Promise<{ ok: true }> {
    return this.makeRequest<{ ok: true }>(`/api/memoir/${encodeURIComponent(memoirId)}/handover`, {
      method: 'POST',
    })
  }

  /** The caller's account closure, or `{ closure: null }` (SOL-201). */
  async getAccountClosure(): Promise<AccountClosureView> {
    return this.makeRequest<AccountClosureView>('/api/account/closure', {})
  }

  /** Ask to delete the account; `email` must be the sign-in address (SOL-201). */
  async requestAccountClosure(email: string): Promise<AccountClosureView> {
    return this.makeRequest<AccountClosureView>('/api/account/closure', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  /** Cancel a pending closure; 409 CLOSURE_IN_PROGRESS once deletion has started. */
  async cancelAccountClosure(): Promise<AccountClosureView> {
    return this.makeRequest<AccountClosureView>('/api/account/closure', { method: 'DELETE' })
  }

  /** A Stripe Billing Portal URL — update card, invoices, resume. */
  async createBillingPortalSession(returnUrl: string): Promise<{ url: string }> {
    return this.makeRequest<{ url: string }>('/api/billing/portal', {
      method: 'POST',
      body: JSON.stringify({ return_url: returnUrl }),
    })
  }

  /**
   * Get URL for a file in an album (for img src) - uses fileId
   */
  getFileUrl(albumUuid: string, fileId: string): string {
    return `${this.baseUrl}/api/album/${albumUuid}/file/${encodeURIComponent(fileId)}`;
  }

  /**
   * Build a cacheable signed <img src> URL for a derivative variant.
   * The `sig`/`rev` come from the file metadata returned by the album
   * endpoints; no Authorization header is needed, so the browser and edge
   * cache the response.
   */
  getVariantUrl(
    albumUuid: string,
    file: { id: string; rev?: number; sig?: string; exp?: number },
    variant: 'thumb' | 'medium' | 'cover',
  ): string {
    const v = file.rev ?? 0
    const sig = encodeURIComponent(file.sig ?? '')
    // `exp` is covered by the signature (SOL-157), so it has to travel with it.
    // Omitting it does not fall back to an unbounded URL — it fails to verify.
    const x = file.exp !== undefined ? `&x=${file.exp}` : ''
    return `${this.baseUrl}/api/album/${albumUuid}/file/${encodeURIComponent(file.id)}/${variant}?v=${v}${x}&sig=${sig}`
  }

  /**
   * Delete photos from an album by fileIds
   */
  async deletePhotos(albumUuid: string, fileIds: string[]): Promise<DeletePhotosResponse> {
    const request: DeletePhotosRequest = { fileIds };
    return this.makeRequest<DeletePhotosResponse>(`/api/album/${albumUuid}/files`, {
      method: 'DELETE',
      body: JSON.stringify(request),
    });
  }

  /**
   * Rename a photo label in an album (KV only, instant)
   */
  async renamePhoto(albumUuid: string, fileId: string, newLabel: string): Promise<RenamePhotoResponse> {
    const request: RenamePhotoRequest = { fileId, newLabel };
    return this.makeRequest<RenamePhotoResponse>(`/api/album/${albumUuid}/files/rename`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Set the album cover (persists coverFileId in KV)
   */
  async setAlbumCover(albumUuid: string, fileId: string): Promise<SetAlbumCoverResponse> {
    const request: SetAlbumCoverRequest = { fileId };
    return this.makeRequest<SetAlbumCoverResponse>(`/api/album/${albumUuid}/files/set-cover`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Delete an album and everything in it.
   *
   * `expectedFileCount` is the number of photos the UI showed the user when
   * they confirmed. The server refuses with 409 FILE_COUNT_MISMATCH if its own
   * count disagrees (SOL-174) — which is what stops a listing that is up to
   * ~60s stale from routing a full album through the one-click confirmation.
   * Optional: omitting it asserts nothing and behaves as this call always did.
   */
  async deleteAlbum(
    albumUuid: string,
    expectedFileCount?: number,
  ): Promise<{ success: boolean; deletedAlbumId: string }> {
    // A query parameter, not a body: request bodies on DELETE are
    // inconsistently supported across the stack and would be exactly the kind
    // of detail that silently stops working.
    const query = expectedFileCount === undefined ? '' : `?expectedFileCount=${expectedFileCount}`;
    return this.makeRequest<{ success: boolean; deletedAlbumId: string }>(
      `/api/album/${albumUuid}${query}`,
      { method: 'DELETE' },
    );
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string }> {
    return this.makeRequest<{ status: string }>('/health', {}, false);
  }
}

// Create default client instance
const defaultConfig: ApiClientConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787',
};

export const apiClient = new ApiClient(defaultConfig);
export default apiClient; 